import { useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import type { Degree } from "@/models/degree";

interface DegreeChangeDialogProps {
  open: boolean;
  currentDegreeName: string | null;
  degrees: Degree[];
  onConfirm: (newDegreeId: string, eventDate: string, notes?: string) => Promise<void>;
  onCancel: () => void;
}

export function DegreeChangeDialog({
  open,
  currentDegreeName,
  degrees,
  onConfirm,
  onCancel,
}: DegreeChangeDialogProps) {
  const todayIso = new Date().toISOString().split("T")[0];

  const [newDegreeId, setNewDegreeId] = useState("");
  const [eventDate, setEventDate] = useState(todayIso);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [degreeError, setDegreeError] = useState(false);

  const handleConfirm = async () => {
    if (!newDegreeId) { setDegreeError(true); return; }
    setIsSubmitting(true);
    try {
      await onConfirm(newDegreeId, eventDate, notes.trim() || undefined);
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setNewDegreeId("");
    setEventDate(todayIso);
    setNotes("");
    setDegreeError(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) { reset(); onCancel(); }
  };

  const resetAndCancel = () => { reset(); onCancel(); };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Cambio de Grado Ministerial</AlertDialogTitle>
          <AlertDialogDescription>
            Grado actual: <span className="font-medium text-foreground">{currentDegreeName ?? "Sin grado"}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="new-degree">
              Nuevo grado <span className="text-destructive">*</span>
            </Label>
            <Select
              value={newDegreeId}
              onValueChange={(v) => { setNewDegreeId(v); setDegreeError(false); }}
            >
              <SelectTrigger id="new-degree" className={degreeError ? "border-destructive" : ""}>
                <SelectValue placeholder="Seleccionar grado..." />
              </SelectTrigger>
              <SelectContent>
                {degrees.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {degreeError && <p className="text-sm text-destructive">El nuevo grado es obligatorio.</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="degree-date">Fecha del cambio</Label>
            <Input
              id="degree-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              max={todayIso}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="degree-notes">Notas (opcional)</Label>
            <Textarea
              id="degree-notes"
              placeholder="Observaciones adicionales..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={resetAndCancel} disabled={isSubmitting}>
            Cancelar
          </AlertDialogCancel>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
            Confirmar cambio
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
